import React, { FC, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { get, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Elements
import {
  CheckBox,
  SmsConfirmationModal,
  AdditionalCheckButton,
  Input,
  RadioInput,
  Submit,
  SubmitType,
  AdditionalCheckModal,
  TextArea,
} from "../../FormElements";
import { GenericModal } from "../../FormElements/Modal/GenericModal/GenericModal";
import { PhoneConfirmationModal } from "../../FormElements/Modal/ConfirmationModal/PhoneConfirmationModal";
import { WaitWhileSummary } from "../../Summary/WaitWhileSummary";
import { Summary } from "../../Summary/Summary";
import { stepHeadings } from "../../stepHeadings";
import { StepLayout } from "../StepLayout/StepLayout";
import { ErrorMessage } from "../../ErrorMessage/ErrorMessage";
import { RichTextArea } from "../../RichTextArea/RichTextArea";

// Helpers
import { onSubmit, setStorage } from "../../Helpers/formFunctions";
import { thirdStepValidator } from "../../Validation/ThirdStepValidator";
import { isObjectEmpty } from "../../Helpers";
import { FieldStatus } from "../../Helpers/FieldStatus";
import { calculateTotalPrice } from "../../Helpers/totalPriceCalculator";

// Types
import { FromThirdStepData, RootDataType } from "../../Helpers/interfaces";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../../Helpers/formTypes";
import { CreateBookingResponseDto } from "@apiModels/CreateBookingResponseDto.csharp";
import { AppointmentBookingSystem } from "@apiModels/AppointmentBookingSystem.csharp";

// Hooks
import {
  useThirdStepConfirm,
  useThirdStepDropInMetodikaSubmit,
  useThirdStepDropInSubmit,
  useThirdStepSubmit,
  useThirdStepSubmitMetodika,
} from "../../Hooks/useStepData";
import { useLabels } from "../../Hooks/useLabels";
import { GtmService } from "../../GtmService";
import { StorageAccessor } from "../../StorageAccesor/StorageAccesor";
import { BookingStatus } from "../../../../../Api/Model/BookingStatus.csharp";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";
import { useBookingContext } from "../../../BookingButton/BookingContext/BookingContext";
import {
  isUnderage,
  thirdStepDataDropInParser,
  thirdStepDataCrateParser,
  thirdStepDataConfirmParser,
  isOfAge,
  minAgeNotBlocking,
  maxAgeNotBlocking,
  thirdStepDataMetodikaDropInParser,
} from "../../Helpers/personalInfoFormStepHelpers";
import classNames from "classnames";

type PersonalInfoFormStepProps = {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
};

const defaultNotesLength = 400;

export const PersonalInfoFormStep: FC<PersonalInfoFormStepProps> = ({
  state,
  dispatch,
  nextStep,
  rootData,
}) => {
  const { bookingLabels } = useLabels();
  const [isSMSModalOpen, setIsSMSModalOpen] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [
    isPreparationRequirementsModalOpen,
    setIsPreparationRequirementsModalOpen,
  ] = useState<boolean>(false);
  const [
    isDoctorPreparationRequirementsOpen,
    setIsDoctorPreparationRequirementsOpen,
  ] = useState<boolean>(false);
  const [isAgeValidationModalOpen, setIsAgeValidationModalOpen] =
    useState<boolean>(false);
  const [isPhoneConfirmationModalOpen, setIsPhoneConfirmationModalOpen] =
    useState<boolean>(false);

  const [dropInAPIResponse, setDropInAPIResponse] =
    useState<CreateBookingResponseDto>();

  const { waitlist, awaitTime, newBookingFilterValues } = useBookingContext();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const thirdStepLabels = bookingLabels!.stepsBookingLabels.thirdStepLabels;

  const dropdownLabels = thirdStepLabels.dropdownLabels;
  const newBookingStepsLabels =
    bookingLabels!.stepsBookingLabels.newBookingStepsLabels;

  const remoteContact = waitlist?.remoteContact || state.remoteContact;

  const hasRemoteOptions =
    !!remoteContact?.appointmentOptions.length &&
    (state.service.enableVideoTelephoneOption || !!waitlist?.remoteContact);

  const formDefaultValues: Partial<FromThirdStepData> = {
    remoteContact: remoteContact.appointmentOptions.find(
      (opt) => opt.preselected
    )?.label,
    preparationRequirements:
      !!state.appointment.preparationRequirementsDto &&
      state.appointment.preparationRequirementsDto.preselected,
    doctorPreparationRequirements:
      !!state.appointment.doctorPreparationRequirementsDto &&
      state.appointment.doctorPreparationRequirementsDto.preselected,
  };

  const [bookingLoading, setBookingLoading] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationError, setConfirmationError] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [requireParentPersonalIdNumber, setRequireParentPersonalIdNumber] =
    useState(false);
  const [requirePreparationRequirements, setRequirePreparationRequirements] =
    useState(false);
  const [notesLength, setNotesLength] = useState(defaultNotesLength);
  const [ageValidationModalDescription, setAgeValidationModalDescription] =
    useState<string | undefined>(undefined);

  useEffect(() => {
    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);
  }, []);

  useEffect(() => {
    if (!waitlist) {
      setStorage(state);
    }
  }, [state]);

  const { requireParentSsn, blockedSsnList, blockedSsnErrorMessage } =
    useBookingContext();

  const {
    handleSubmit,
    getValues,
    formState: { errors },
    register,
    setValue,
    watch,
    trigger,
  } = useForm<FromThirdStepData>({
    defaultValues: formDefaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(
      thirdStepValidator(
        state,
        bookingLabels!.bookingErrors,
        waitlist,
        requireParentPersonalIdNumber,
        thirdStepLabels,
        requirePreparationRequirements,
        blockedSsnList,
        blockedSsnErrorMessage,
        hasRemoteOptions
      )
    ),
  });

  useEffect(() => {
    const personalNumber = `${getValues().personalIdNumber}`;
    const minMaxAge = state.appointment.minMaxAge;
    const minMaxAgeNotBlocking =
      minMaxAge == undefined ||
      (minAgeNotBlocking(personalNumber, minMaxAge) &&
        maxAgeNotBlocking(personalNumber, minMaxAge));
    if (
      !!state.appointment.preparationRequirementsDto &&
      !minMaxAge?.showPreparationRequirementsOnly
    )
      setRequirePreparationRequirements(true);
    else setRequirePreparationRequirements(false);

    if (
      requireParentSsn &&
      isUnderage(personalNumber) &&
      minMaxAgeNotBlocking
    ) {
      setRequireParentPersonalIdNumber(true);
      setNotesLength(370);
    } else {
      setRequireParentPersonalIdNumber(false);
      setNotesLength(defaultNotesLength);
    }

    if (minMaxAge?.showPreparationRequirementsOnly)
      setRequirePreparationRequirements(shouldShowPreparationRequirement());
  }, [getValues, state.appointment, watch("personalIdNumber")]);

  const thirdStepSubmit = async (props: FromThirdStepData) => {
    GtmService.personalInfoStepSubmit(
      state.specializationName,
      state.service.specializationId,
      state.service.bookingName,
      state.service.id,
      state.location.name,
      state.location.id,
      state.doctor.name,
      state.doctor.id,
      state.appointment.startTime,
      props.subscribeToNewsletter,
      calculateTotalPrice(state.pricingSummary),
      newBookingFilterValues?.breadcrumbsShown && state.breadcrumbs.length > 0
    );

    const minimumAge = state.appointment.minMaxAge?.minAge ?? 0;
    const minAgeShowWarning = state.appointment.minMaxAge?.showWarningOnly;
    const maximumAge = state.appointment.minMaxAge?.maxAge ?? 150;
    const maxAgeShowWarning = state.appointment.minMaxAge?.showWarningOnly;
    if (
      minAgeShowWarning &&
      isUnderage(`${props.personalIdNumber}`, minimumAge)
    ) {
      setAgeValidationModalDescription(
        state.appointment.minMaxAge?.minAgeModal?.description
      );
      setIsAgeValidationModalOpen(true);
      return;
    } else if (
      maxAgeShowWarning &&
      isOfAge(`${props.personalIdNumber}`, maximumAge)
    ) {
      setAgeValidationModalDescription(
        state.appointment.minMaxAge?.maxAgeModal?.description
      );
      setIsAgeValidationModalOpen(true);
      return;
    } else {
      await createBooking(props);
      GtmService.smsRequested();
    }
  };

  const createMetodikaBooking = async (props: FromThirdStepData) => {
    const remoteOption = hasRemoteOptions
      ? remoteContact.appointmentOptions.find(
          (opt) => opt.label.toLowerCase() == props.remoteContact
        )
      : undefined;
    const bookingRequest = thirdStepDataCrateParser(
      props,
      state,
      requireParentPersonalIdNumber,
      remoteOption
    );

    const bookingRequestWaitWhile =
      waitlist &&
      thirdStepDataMetodikaDropInParser(
        props,
        waitlist.contentId,
        requireParentPersonalIdNumber
      );

    try {
      let bookingResponse;
      setBookingLoading(true);
      if (waitlist?.isMetodika) {
        bookingResponse = await useThirdStepDropInMetodikaSubmit(
          bookingRequestWaitWhile
        );
      } else {
        bookingResponse = await useThirdStepSubmitMetodika(bookingRequest);
      }
      setBookingLoading(false);
      dispatch({
        type: FormReducer.update,
        field: FormFields.bookingResponse,
        payload: bookingResponse,
      });
      onSubmit(nextStep, errors);
      clearStorage();
      GtmService.orderConfirmed(
        state,
        rootData.centers.find((c) => c.id == state.appointment.centerId)
      );
    } catch (err: any) {
      setBookingLoading(false);
      setBookingError(err.message);
      GtmService.bookingCreationError(
        state.service.name,
        state.specializationName,
        state.location.name,
        err.message
      );
    }

    setConfirmationLoading(false);
  };

  const createBooking = async (props: FromThirdStepData) => {
    setIsAgeValidationModalOpen(false);
    setConfirmationError("");

    if (!isObjectEmpty(errors)) return;

    setConfirmationLoading(true);

    const remoteOption = hasRemoteOptions
      ? remoteContact.appointmentOptions.find(
          (opt) => opt.label.toLowerCase() === props.remoteContact
        )
      : undefined;

    try {
      if (waitlist?.contentId) {
        if (waitlist.isMetodika) {
          setIsPhoneConfirmationModalOpen(true);
          setConfirmationLoading(false);
          return;
        } else {
          const dropInRequest = thirdStepDataDropInParser(
            props,
            waitlist.contentId,
            requireParentPersonalIdNumber,
            remoteOption
          );
          const dropInResponse = await useThirdStepDropInSubmit(dropInRequest);

          if (dropInResponse.bookingStatus === BookingStatus.Pending) {
            setDropInAPIResponse(dropInResponse);
            dispatch({
              type: FormReducer.update,
              field: FormFields.bookingSessionId,
              payload: dropInResponse.bookingSessionId,
            });
            setIsSMSModalOpen(true);
          } else {
            setConfirmationError(dropInResponse.message);
          }
          setConfirmationLoading(false);
          return;
        }
      }
      const bookingRequest = thirdStepDataCrateParser(
        props,
        state,
        requireParentPersonalIdNumber,
        remoteOption
      );

      if (
        state.appointment.appointmentBookingSystem ===
        AppointmentBookingSystem.Dips
      ) {
        const bookingResponse = await useThirdStepSubmit(bookingRequest);
        if (bookingResponse.bookingStatus === BookingStatus.Pending) {
          dispatch({
            type: FormReducer.update,
            field: FormFields.bookingSessionId,
            payload: bookingResponse.bookingSessionId,
          });
          setIsSMSModalOpen(true);
        }
      } else if (
        state.appointment.appointmentBookingSystem ===
        AppointmentBookingSystem.Metodika
      ) {
        setIsPhoneConfirmationModalOpen(true);
      } else {
        console.warn("Unknow booking provider");
      }
    } catch (err: any) {
      setBookingError(err.message);
      GtmService.bookingCreationError(
        state.service.name,
        state.specializationName,
        state.location.name,
        err.message
      );
    } finally {
      setConfirmationLoading(false);
    }
  };

  const closeSmsModal = () => {
    setIsSMSModalOpen(false);
    setConfirmationLoading(false);
  };

  async function confirmBooking(code?: number) {
    if (code) {
      let request;
      const props = getValues();

      const remoteOption = hasRemoteOptions
        ? remoteContact.appointmentOptions.find(
            (opt) => opt.label.toLowerCase() == props.remoteContact
          )
        : undefined;
      if (waitlist?.contentId) {
        request = thirdStepDataDropInParser(
          props,
          waitlist?.contentId,
          requireParentPersonalIdNumber,
          remoteOption,
          code,
          dropInAPIResponse
        );
      } else {
        request = thirdStepDataConfirmParser(
          props,
          state,
          requireParentPersonalIdNumber,
          remoteOption,
          code
        );
      }

      try {
        setBookingLoading(true);
        const response = await useThirdStepConfirm(request);
        setBookingLoading(false);

        if (response.bookingStatus == BookingStatus.Confirmed) {
          dispatch({
            type: FormReducer.update,
            field: FormFields.bookingResponse,
            payload: response,
          });

          onSubmit(nextStep, errors);
          clearStorage();

          if (waitlist?.contentId) {
            GtmService.dropInConfirmed(
              waitlist.serviceName,
              waitlist.centerName,
              state.bookingResponse.bookingSessionId
            );
          } else {
            GtmService.orderConfirmed(
              state,
              rootData.centers.find((c) => c.id == state.appointment.centerId)
            );
          }
        }
        if (
          response.bookingStatus == BookingStatus.Pending ||
          response.bookingStatus == BookingStatus.Invalid
        ) {
          setConfirmationError(response.message);
        }
      } catch (err: any) {
        setConfirmationError(err.message);
        setBookingLoading(false);
        GtmService.confirmationError(
          state.service.name,
          state.specializationName,
          state.location.name,
          err.message
        );
      }
    }
  }

  const clearStorage = () => {
    new StorageAccessor().removeItem();
  };

  const shouldShowPreparationRequirement = () => {
    const ssn = getValues("personalIdNumber");
    const preparationRequirementsAvailable =
      !!state.appointment.preparationRequirementsDto;

    if (
      state.appointment.minMaxAge?.showPreparationRequirementsOnly &&
      preparationRequirementsAvailable
    ) {
      return (
        isUnderage(`${ssn}`, state.appointment.minMaxAge?.minAge || 0) ||
        isOfAge(`${ssn}`, state.appointment.minMaxAge?.maxAge || 150)
      );
    }

    return preparationRequirementsAvailable;
  };

  const isWaitlist = !!waitlist?.contentId;

  const determineButtonLabel = isWaitlist
    ? "Fortsett"
    : thirdStepLabels?.nextButtonLabel;

  const personalInfoFormStepCustomHeading =
    bookingLabels?.stepsBookingLabels?.newBookingStepsLabels
      .personalInfoStepHeading;
  const personalInfoFormStepHeading =
    personalInfoFormStepCustomHeading ||
    stepHeadings.personalInfoFormStepHeading;

  return (
    <>
      <div ref={wrapperRef} className={classNames("PersonalInfoFormStep")}>
        <StepLayout
          heading={
            isWaitlist ? "Dine opplysninger" : personalInfoFormStepHeading
          }
        >
          {!waitlist?.contentId && (
            <RichTextArea
              innerClassName="mb-3 booking-narrow-item"
              content={thirdStepLabels?.optionalDescription}
            />
          )}
          <form
            onSubmit={handleSubmit(thirdStepSubmit)}
            className="d-flex flex-column"
          >
            {state.appointment.appointmentBookingSystem ===
              AppointmentBookingSystem.Dips && !waitlist?.isMetodika ? (
              <Input
                name="name"
                label={dropdownLabels.name}
                getValues={getValues}
                error={errors.name}
                register={register}
                placeholder={
                  newBookingStepsLabels.personalInfoStepNamePlaceholder
                }
              />
            ) : (
              <>
                <Input
                  name="firstName"
                  label={dropdownLabels.firstName}
                  getValues={getValues}
                  error={errors.firstName}
                  register={register}
                  placeholder={
                    newBookingStepsLabels.personalInfoStepFirstNamePlaceholder
                  }
                />
                <Input
                  name="lastName"
                  label={dropdownLabels.lastName}
                  getValues={getValues}
                  error={errors.lastName}
                  register={register}
                  placeholder={
                    newBookingStepsLabels.personalInfoStepLastNamePlaceholder
                  }
                />
              </>
            )}

            <Input
              name="personalIdNumber"
              label={dropdownLabels.personalIdNumber}
              inputmode={"numeric"}
              type="number"
              getValues={getValues}
              error={errors.personalIdNumber}
              register={register}
              placeholder={newBookingStepsLabels.personalInfoStepSsnPlaceholder}
            />
            {requireParentPersonalIdNumber && (
              <Input
                name="parentPersonalIdNumber"
                label={dropdownLabels.parentPersonalIdNumber}
                inputmode={"numeric"}
                type="number"
                getValues={getValues}
                error={errors.parentPersonalIdNumber}
                register={register}
                placeholder={
                  newBookingStepsLabels.personalInfoStepSsnPlaceholder
                }
              />
            )}
            <Input
              name="mobileNumber"
              label={dropdownLabels.mobileNumber}
              inputmode={"tel"}
              type="tel"
              getValues={getValues}
              error={errors.mobileNumber}
              register={register}
              placeholder={
                newBookingStepsLabels.personalInfoStepMobilePlaceholder
              }
            />

            <Input
              name="emailAddress"
              label={dropdownLabels.emailAddress}
              inputmode={"email"}
              type="email"
              getValues={getValues}
              error={errors.emailAddress}
              register={register}
              placeholder={
                newBookingStepsLabels.personalInfoStepEmailPlaceholder
              }
            />
            {hasRemoteOptions && (
              <RadioInput
                getValues={getValues}
                name="remoteContact"
                label={remoteContact.label}
                options={remoteContact.appointmentOptions}
                register={register}
                error={errors.remoteContact}
              />
            )}
            <TextArea
              label={
                !state.appointment.notesLabel
                  ? dropdownLabels.notes
                  : state.appointment.notesLabel
              }
              name="notes"
              error={errors.notes}
              register={register}
              maxLength={notesLength}
              getValues={getValues}
            />
            <CheckBox
              name="subscribeToNewsletter"
              label={dropdownLabels.subscribeToNewsletter}
              error={errors.subscribeToNewsletter}
              register={register}
              trigger={trigger}
            />
            {!waitlist?.contentId && (
              <CheckBox
                label={
                  <AdditionalCheckButton
                    label={dropdownLabels.termsLabel}
                    buttonLabel={
                      dropdownLabels.termsButton ?? "bestillingsvilkårene"
                    }
                    onClickFunction={() => setIsTermsModalOpen(true)}
                  />
                }
                name="terms"
                error={errors.terms}
                register={register}
                trigger={trigger}
              />
            )}

            {dropdownLabels.consent && (
              <CheckBox
                name="consent"
                label={dropdownLabels.consent}
                error={errors.consent}
                register={register}
                trigger={trigger}
              />
            )}
            {shouldShowPreparationRequirement() && (
              <CheckBox
                label={
                  <AdditionalCheckButton
                    label={state.appointment.preparationRequirementsDto!.label}
                    buttonLabel={
                      state.appointment.preparationRequirementsDto!.button
                    }
                    onClickFunction={() =>
                      setIsPreparationRequirementsModalOpen(true)
                    }
                  />
                }
                name="preparationRequirements"
                error={errors.preparationRequirements}
                register={register}
                trigger={trigger}
              />
            )}
            {!!state.appointment.doctorPreparationRequirementsDto &&
              !!state.appointment.doctorPreparationRequirementsDto.label && (
                <CheckBox
                  label=""
                  name="doctorPreparationRequirements"
                  register={register}
                  error={errors.doctorPreparationRequirements}
                  trigger={trigger}
                >
                  <AdditionalCheckButton
                    label={
                      state.appointment.doctorPreparationRequirementsDto.label
                    }
                    buttonLabel={
                      state.appointment.doctorPreparationRequirementsDto.button
                    }
                    onClickFunction={() =>
                      setIsDoctorPreparationRequirementsOpen(true)
                    }
                  />
                </CheckBox>
              )}
            {bookingError && <ErrorMessage message={bookingError} />}
            {waitlist?.contentId ? (
              <div className="mt-4">
                <WaitWhileSummary waitlist={waitlist} awaitTime={awaitTime} />
              </div>
            ) : (
              <Summary state={state} step={4} rootData={rootData} />
            )}
            <Submit
              className="PersonalInfoFormStep__submitButton"
              type={SubmitType.submit}
              status={
                confirmationLoading && !bookingError
                  ? FieldStatus.disabled
                  : FieldStatus.active
              }
            >
              {confirmationLoading ? (
                <LoadingAnimation text={determineButtonLabel} />
              ) : (
                determineButtonLabel
              )}
            </Submit>
          </form>
          {isSMSModalOpen && (
            <SmsConfirmationModal
              onSubmit={confirmBooking}
              onClose={closeSmsModal}
              isOpen={isSMSModalOpen}
              loading={bookingLoading}
              confirmationError={confirmationError}
              heading={
                !waitlist?.contentId
                  ? thirdStepLabels.smsConfirmationHeading
                  : "Bekreft registreringen din"
              }
              description={thirdStepLabels.smsConfirmation.replace(
                "$sms",
                `${getValues("mobileNumber")}`
              )}
              inputLabel={thirdStepLabels.smsInputLabel}
              buttonLabel={thirdStepLabels.smsConfirmationButton}
            />
          )}
          {isPhoneConfirmationModalOpen && (
            <PhoneConfirmationModal
              onSubmit={createMetodikaBooking}
              onClose={() => {
                setIsPhoneConfirmationModalOpen(false);
                setConfirmationLoading(false);
              }}
              isOpen={isPhoneConfirmationModalOpen}
              getFormData={getValues}
              setValue={setValue}
              heading={thirdStepLabels.confirmPhoneNumberHeading}
              description={thirdStepLabels.confirmPhoneNumberIntro}
              inputLabel={thirdStepLabels.confirmPhoneNumberLabel}
              buttonLabel={thirdStepLabels.confirmPhoneNumberButton}
              loading={bookingLoading}
            />
          )}
          <AdditionalCheckModal
            onClose={() => setIsTermsModalOpen(false)}
            isOpen={isTermsModalOpen}
            policy={thirdStepLabels.terms}
          />
          <AdditionalCheckModal
            onClose={() => setIsPreparationRequirementsModalOpen(false)}
            isOpen={isPreparationRequirementsModalOpen}
            policy={state.appointment.preparationRequirementsDto?.content ?? ""}
          />
          <AdditionalCheckModal
            onClose={() => setIsDoctorPreparationRequirementsOpen(false)}
            isOpen={isDoctorPreparationRequirementsOpen}
            policy={
              state.appointment.doctorPreparationRequirementsDto?.content ?? ""
            }
          />
          {state.appointment.minMaxAge?.showWarningOnly && (
            <GenericModal
              className="PersonalInfoFormStep__ageValidationModal"
              onSubmit={handleSubmit(createBooking)}
              onClose={() => setIsAgeValidationModalOpen(false)}
              description={ageValidationModalDescription}
              buttonLabel="Fortsett"
              isOpen={isAgeValidationModalOpen}
            />
          )}
        </StepLayout>
      </div>
    </>
  );
};
